import React, { ChangeEvent, FormEvent, useEffect, useMemo, useState } from 'react';
import { RouteComponentProps, useParams } from 'react-router-dom';
import logoLadoBom from '../../resources/imgs/logo-lado-bom-seguros.svg';
import logoCreditoReal from '../../resources/imgs/logo-credito-real.png';
import {
  calculate,
  CalculatorInput,
  CalculationResult,
  changeOccupation,
  defaults,
  getRegion,
  StateCode
} from './calculator';
import { saveCreditoRealCalculation, SaveCalculationResponse } from './service';
import {
  BrandGroup,
  CoverageTable,
  CreditLogo,
  Divider,
  ErrorText,
  Field,
  FieldGrid,
  Footer,
  Header,
  HeaderMeta,
  Input,
  Layout,
  Logo,
  Modal,
  ModalActions,
  ModalBackdrop,
  Notice,
  Page,
  PrimaryButton,
  ResultPanel,
  Section,
  SectionHeader,
  SecondaryButton,
  Select,
  Subsection,
  SummaryRow,
  Toggle,
  ToggleGrid,
  Total
} from './styles';

const currency = (value: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
    value || 0
  );

const numberValue = (event: ChangeEvent<HTMLInputElement>) =>
  Number(event.target.value || 0);

const getErrorMessage = (
  error: Error | { message?: string | string[] } | string | null | undefined
) => {
  if (typeof error === 'string') return error;
  if (error && Array.isArray(error.message)) return error.message.join(', ');
  if (error && typeof error.message === 'string') return error.message;
  return 'Não foi possível gravar a cotação. Tente novamente.';
};

interface ContactForm {
  propertyCode: string;
  broker: string;
  tenant: string;
  cpf: string;
}

const initialContact: ContactForm = { propertyCode: '', broker: '', tenant: '', cpf: '' };

const CreditoRealFireCalculator: React.FC<
  RouteComponentProps<{ state?: string }>
> = () => {
  const { state: routeState } = useParams<{ state?: string }>();
  const state: StateCode = (routeState || 'RS').toUpperCase() === 'SC' ? 'SC' : 'RS';
  const region = getRegion(state);
  const [input, setInput] = useState<CalculatorInput>(() => defaults(state));
  const [saveOpen, setSaveOpen] = useState(false);
  const [contact, setContact] = useState<ContactForm>(initialContact);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saved, setSaved] = useState<SaveCalculationResponse | null>(null);

  useEffect(() => {
    setInput(defaults(state));
    setSaved(null);
    setSaveOpen(false);
  }, [state]);

  const calculationState = useMemo<{
    result: CalculationResult | null;
    error: string;
  }>(() => {
    try {
      return { result: calculate(input), error: '' };
    } catch (error) {
      return { result: null, error: getErrorMessage(error) };
    }
  }, [input]);
  const calculation = calculationState.result;
  const calculationError = calculationState.error;

  const setValue = (patch: Partial<CalculatorInput>) => {
    setSaved(null);
    setInput(previous => ({ ...previous, ...patch }));
  };

  const updateContact =
    (field: keyof ContactForm) => (event: ChangeEvent<HTMLInputElement>) => {
      setContact(previous => ({ ...previous, [field]: event.target.value }));
      setSaveError('');
    };

  const handleSave = async (event: FormEvent) => {
    event.preventDefault();
    if (!calculation) return;
    const cpf = contact.cpf.replace(/\D/g, '');
    if (
      !contact.propertyCode.trim() ||
      !contact.broker.trim() ||
      !contact.tenant.trim() ||
      cpf.length !== 11
    ) {
      setSaveError('Informe código do imóvel, corretor, inquilino e um CPF válido.');
      return;
    }
    setSaving(true);
    setSaveError('');
    try {
      const response = await saveCreditoRealCalculation(input, calculation, {
        ...contact,
        propertyCode: contact.propertyCode.trim(),
        broker: contact.broker.trim(),
        tenant: contact.tenant.trim(),
        cpf
      });
      setSaved(response);
      setSaveOpen(false);
    } catch (error) {
      setSaveError(getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const propertyInput =
    input.mode === 'area' ? (
      <Field>
        Área construída (m²)
        <Input
          type="number"
          min="1"
          step="1"
          value={input.area || ''}
          onChange={event => setValue({ area: numberValue(event) })}
        />
        <small>
          Valor do prédio: {currency(calculation ? calculation.buildingAmount : 0)}
        </small>
      </Field>
    ) : (
      <Field>
        Importância segurada - prédio
        <Input
          type="number"
          min="1"
          step="100"
          value={input.buildingValue || ''}
          onChange={event => setValue({ buildingValue: numberValue(event) })}
        />
        <small>Informe o valor manualmente quando a área não for suficiente.</small>
      </Field>
    );

  return (
    <Page>
      <Header>
        <BrandGroup>
          <Logo src={logoLadoBom} alt="Lado Bom Seguros" />
          <Divider />
          <CreditLogo src={logoCreditoReal} alt="Crédito Real" />
        </BrandGroup>
        <HeaderMeta>
          <strong>Calculadora de cotação</strong>
          Seguro Incêndio · {region.name} · Regras {input.state}
        </HeaderMeta>
      </Header>

      <Layout>
        <Section>
          <SectionHeader>
            <h1>Dados do imóvel</h1>
            <p>Preencha os dados abaixo para consultar um valor aproximado do seguro.</p>
          </SectionHeader>

          <FieldGrid>
            <Field>
              Ocupação
              <Select
                value={input.occupationId}
                onChange={event => setInput(changeOccupation(input, event.target.value))}
              >
                {region.occupations.map(occupation => (
                  <option key={occupation.id} value={occupation.id}>
                    {occupation.label}
                  </option>
                ))}
              </Select>
            </Field>
            <Field>
              Base do cálculo
              <Select
                value={input.mode}
                onChange={event =>
                  setValue({ mode: event.target.value as CalculatorInput['mode'] })
                }
              >
                <option value="area">Área construída</option>
                <option value="manual">Valor do prédio</option>
              </Select>
            </Field>
            {propertyInput}
          </FieldGrid>

          <Subsection>
            <SectionHeader>
              <h2>Coberturas adicionais</h2>
              <p>Ative as coberturas e ajuste os limites desejados.</p>
            </SectionHeader>
            <ToggleGrid>
              <Toggle active={input.wind}>
                <input
                  type="checkbox"
                  checked={input.wind}
                  onChange={event => setValue({ wind: event.target.checked })}
                />
                <div>
                  Vendaval / Granizo<span>Limite conforme a ocupação</span>
                </div>
              </Toggle>
              <Toggle active={input.pipes}>
                <input
                  type="checkbox"
                  checked={input.pipes}
                  onChange={event => setValue({ pipes: event.target.checked })}
                />
                <div>
                  Tubulações<span>Até 10% do prédio, limitado a R$ 50 mil</span>
                </div>
              </Toggle>
              <Toggle active={input.glassEnabled}>
                <input
                  type="checkbox"
                  checked={input.glassEnabled}
                  onChange={event => setValue({ glassEnabled: event.target.checked })}
                />
                <div>
                  Quebra de vidros<span>Escolha o limite abaixo</span>
                </div>
              </Toggle>
            </ToggleGrid>
          </Subsection>

          <Subsection>
            <FieldGrid>
              <Field>
                Danos elétricos (R$)
                <Input
                  type="number"
                  min="3000"
                  step="500"
                  value={input.electrical || ''}
                  onChange={event => setValue({ electrical: numberValue(event) })}
                />
                <small>Sugestão mínima de R$ 3.000. O limite depende da ocupação.</small>
              </Field>
              <Field>
                Responsabilidade civil (R$)
                <Input
                  type="number"
                  min="20000"
                  step="1000"
                  value={input.liability || ''}
                  disabled={
                    !region.occupations.find(item => item.id === input.occupationId)
                      ?.residential
                  }
                  onChange={event => setValue({ liability: numberValue(event) })}
                />
                <small>Disponível para ocupações residenciais.</small>
              </Field>
              {input.glassEnabled && (
                <Field>
                  Quebra de vidros (R$)
                  <Input
                    type="number"
                    min="2000"
                    step="500"
                    value={input.glass || ''}
                    onChange={event => setValue({ glass: numberValue(event) })}
                  />
                  <small>Sugestão mínima de R$ 2.000.</small>
                </Field>
              )}
            </FieldGrid>
          </Subsection>

          <Subsection>
            <Field>
              Assistência 24h
              <Select
                value={input.assistance}
                onChange={event =>
                  setValue({
                    assistance: event.target.value as CalculatorInput['assistance']
                  })
                }
              >
                <option value="basic">
                  Básico - {currency(region.assistance.basic)}
                </option>
                <option value="complete">
                  Completo - {currency(region.assistance.complete)}
                </option>
                <option value="business">
                  Empresa - {currency(region.assistance.business)}
                </option>
              </Select>
            </Field>
          </Subsection>

          {calculationError && <ErrorText role="alert">{calculationError}</ErrorText>}
        </Section>

        <ResultPanel>
          <SectionHeader>
            <h2>Resumo da cotação</h2>
            <p>Valor aproximado para análise comercial.</p>
          </SectionHeader>
          <Total>
            <div className="label">Prêmio total com IOF</div>
            <strong>{currency(calculation ? calculation.total : 0)}</strong>
          </Total>
          {calculation && (
            <>
              <SummaryRow>
                <span>Importância segurada - prédio</span>
                <strong>{currency(calculation.buildingAmount)}</strong>
              </SummaryRow>
              <SummaryRow>
                <span>Prêmio líquido</span>
                <strong>{currency(calculation.net)}</strong>
              </SummaryRow>
              <SummaryRow>
                <span>IOF (7,38%)</span>
                <strong>{currency(calculation.tax)}</strong>
              </SummaryRow>
              <CoverageTable>
                {calculation.coverages.map(coverage => (
                  <div className="coverage" key={coverage.id}>
                    <div className="coverage-head">
                      <span>{coverage.label}</span>
                      <strong>{currency(coverage.premium)}</strong>
                    </div>
                    <div className="coverage-meta">
                      Importância segurada: {currency(coverage.insuredAmount)}
                    </div>
                  </div>
                ))}
                <div className="coverage">
                  <div className="coverage-head">
                    <span>Assistência 24h ({calculation.assistance.label})</span>
                    <strong>{currency(calculation.assistance.premium)}</strong>
                  </div>
                </div>
              </CoverageTable>
            </>
          )}
          <PrimaryButton
            type="button"
            disabled={!calculation}
            onClick={() => {
              setSaveError('');
              setSaveOpen(true);
            }}
          >
            Gravar cálculo
          </PrimaryButton>
          {saved && (
            <Notice success>
              Cotação gravada com o número <strong>{saved.id}</strong> em{' '}
              {new Date(saved.savedAt).toLocaleString('pt-BR')}.
              {saved.spreadsheetStatus === 'not_configured' &&
                ' O envio para a planilha ainda aguarda configuração no servidor.'}
            </Notice>
          )}
        </ResultPanel>
      </Layout>

      <Footer>
        Esta calculadora apresenta uma estimativa. A contratação depende da conferência
        dos dados e das condições vigentes da seguradora.
      </Footer>

      {saveOpen && calculation && (
        <ModalBackdrop role="presentation">
          <Modal role="dialog" aria-modal="true" aria-labelledby="save-calculation-title">
            <h2 id="save-calculation-title">Identifique a cotação</h2>
            <p>
              Esses dados serão gravados junto com a cotação para contratação posterior.
            </p>
            <form onSubmit={handleSave}>
              <FieldGrid>
                <Field>
                  Código do imóvel
                  <Input
                    required
                    value={contact.propertyCode}
                    onChange={updateContact('propertyCode')}
                  />
                </Field>
                <Field>
                  Nome do corretor
                  <Input
                    required
                    value={contact.broker}
                    onChange={updateContact('broker')}
                  />
                </Field>
                <Field>
                  Nome do inquilino
                  <Input
                    required
                    value={contact.tenant}
                    onChange={updateContact('tenant')}
                  />
                </Field>
                <Field>
                  CPF do inquilino
                  <Input
                    required
                    inputMode="numeric"
                    maxLength={14}
                    value={contact.cpf}
                    onChange={updateContact('cpf')}
                  />
                </Field>
              </FieldGrid>
              {saveError && <ErrorText role="alert">{saveError}</ErrorText>}
              <ModalActions>
                <SecondaryButton type="button" onClick={() => setSaveOpen(false)}>
                  Cancelar
                </SecondaryButton>
                <PrimaryButton type="submit" disabled={saving}>
                  {saving ? 'Gravando...' : 'Confirmar gravação'}
                </PrimaryButton>
              </ModalActions>
            </form>
          </Modal>
        </ModalBackdrop>
      )}
    </Page>
  );
};

export default CreditoRealFireCalculator;
